import { HttpError } from '../../core/errors.js';

export const MAX_CSV_BYTES = 5 * 1024 * 1024;
export const MAX_CSV_ROWS = 5000;

/** RFC 4180 parser: quoted fields, escaped quotes, embedded newlines, CRLF, UTF-8 BOM. */
export function parseCsv(text: string): string[][] {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"' && field === '') {
      inQuotes = true;
      i += 1;
    } else if (ch === ',') {
      row.push(field);
      field = '';
      i += 1;
    } else if (ch === '\r' || ch === '\n') {
      row.push(field);
      field = '';
      if (!(row.length === 1 && row[0] === '')) rows.push(row);
      row = [];
      i += ch === '\r' && text[i + 1] === '\n' ? 2 : 1;
    } else {
      field += ch;
      i += 1;
    }
  }
  if (inQuotes)
    throw new HttpError(
      400,
      'BAD_CSV',
      'The file has an unclosed quote. Check the last quoted value.',
    );
  if (field !== '' || row.length > 0) {
    row.push(field);
    if (!(row.length === 1 && row[0] === '')) rows.push(row);
  }
  return rows;
}

const NEEDS_ESCAPE = /[",\r\n]/;

/** Spreadsheet formula injection defence (CLAUDE.md rule 9): cells starting with = + - @ are prefixed with '. */
export function neutraliseCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const text = value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}

/** Reverses neutraliseCell so an exported file re-imports to the same values. */
export const unneutraliseCell = (text: string) =>
  /^'[=+\-@\t\r]/.test(text) ? text.slice(1) : text;

export function csvLine(cells: unknown[]): string {
  return cells
    .map((c) => {
      const s = neutraliseCell(c);
      return NEEDS_ESCAPE.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    })
    .join(',');
}

export const toCsv = (header: string[], rows: unknown[][]): string =>
  [csvLine(header), ...rows.map(csvLine)].join('\r\n') + '\r\n';
